/**
 * Move history, newest first. New entries slide in; each play lists its words
 * with their points; the running score is shown for context.
 */
import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx';
import type { MoveRecord, Seat } from '@wordloom/engine';
import { moveLabel } from '../game/events';
import { formatScorePair } from '../lib/format';
import { IconExchange, IconFlag, IconPass, IconSparkle } from './Icons';
import './MoveHistory.css';

interface HistoryProps {
  history: readonly MoveRecord[];
  names: [string, string];
  you: Seat;
}

function MoveBody({ record }: { record: MoveRecord }) {
  switch (record.type) {
    case 'play':
      return (
        <span className="move__words">
          {record.words.map((w, i) => (
            <span key={i} className={clsx('move__word', i === 0 && 'is-main')}>
              {w.word}
              <span className="move__word-pts">{w.score}</span>
            </span>
          ))}
          {record.bingo && (
            <span className="move__bingo">
              <IconSparkle /> Bingo
            </span>
          )}
        </span>
      );
    case 'exchange':
      return (
        <span className="move__note">
          <IconExchange /> Exchanged {record.exchanged} {record.exchanged === 1 ? 'tile' : 'tiles'}
        </span>
      );
    case 'pass':
      return (
        <span className="move__note">
          <IconPass /> Passed
        </span>
      );
    case 'timeout':
      return (
        <span className="move__note">
          <IconPass /> Ran out of time
        </span>
      );
    case 'resign':
      return (
        <span className="move__note">
          <IconFlag /> Resigned
        </span>
      );
    case 'forfeit':
      return (
        <span className="move__note">
          <IconFlag /> Forfeited
        </span>
      );
  }
}

export function MoveHistoryList({ history, names, you }: HistoryProps) {
  if (history.length === 0) {
    return (
      <p className="history__empty">
        No moves yet — the first word must cover the <span className="history__star">★</span>.
      </p>
    );
  }
  const newestFirst = [...history].reverse();
  return (
    <ol className="history__list" aria-label="Moves, newest first" reversed>
      <AnimatePresence initial={false}>
        {newestFirst.map((r) => {
          const name = names[r.seat];
          const mine = r.seat === you;
          return (
            <motion.li
              key={`${r.turn}-${r.seat}-${r.type}`}
              layout="position"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
              className={clsx('move', `move--seat${r.seat}`, mine && 'is-mine', `move--${r.type}`)}
            >
              <span className="sr-only">{moveLabel(r, mine ? 'You' : name)}</span>
              <span className="move__who" aria-hidden="true">
                <span className="move__dot" />
                <span className="move__name">{mine ? 'You' : name}</span>
                <span className="move__turn">#{r.turn}</span>
              </span>
              <span className="move__body" aria-hidden="true">
                <MoveBody record={r} />
              </span>
              <span className="move__score tabular" aria-hidden="true">
                {r.type === 'play' ? `+${r.score}` : '—'}
              </span>
              <span className="move__running tabular" aria-hidden="true">
                {formatScorePair(r.scoresAfter[you], r.scoresAfter[you === 0 ? 1 : 0])}
              </span>
            </motion.li>
          );
        })}
      </AnimatePresence>
    </ol>
  );
}

export function MoveHistory(props: HistoryProps) {
  return (
    <section className="card history" aria-labelledby="history-title">
      <header className="history__head">
        <h2 id="history-title" className="eyebrow">
          History
        </h2>
        {props.history.length > 0 && (
          <span className="history__count">
            {props.history.length} {props.history.length === 1 ? 'move' : 'moves'}
          </span>
        )}
      </header>
      <div className="history__scroll">
        <MoveHistoryList {...props} />
      </div>
    </section>
  );
}
